import _ from "lodash";
import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import { TreeSelect, TreeSelectProps } from "primereact/treeselect";
import React from "react";
import { QueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../../api/candidate";
import { GetKnowledgeForCandidateResponse } from "../../../../api/candidate/getKnowledgeForCandidate";
import { GetTypesResponse } from "../../../../api/types/getTypes";
import {
  treeSelectOnChange,
  useDefaultTreeSelectProps,
} from "../../../../Components/treeSelectUtils";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { Store2 } from "../../../../WSUtils/Store";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../../WSUtils/UserInfo";
import { concatClassnames, idCounter } from "../../../../WSUtils/util";
import { CVEditContextStore } from "../../CVEditContext";
import { cveField, cveFieldStore } from "../../Fields/CVEditBaseField";
import { separateLanguageAndKnowledge } from "../separateLanguageAndKnowledge";
import { CVEditKnowledge } from "./CVEditKnowledge";

export class CVEditKnowledgeSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable knowledgeListForcedHeight: string = undefined;

  @observable knowledge: GetKnowledgeForCandidateResponse;

  @computed get nTotalListItems() {
    return this.knowledgeField.store.value.length;
  }

  @observable knowledgeTypes: GetTypesResponse = null;

  @computed get groupedKnowledgeTypes() {
    const [groupedLanguageTypes, groupedKnowledgeTypes] =
      separateLanguageAndKnowledge(this.knowledgeTypes);

    return groupedKnowledgeTypes;
  }

  @observable wasListEmptyBeforePanelShow = false;
  @observable isPanelShowing = false;

  @computed get showPlaceholder() {
    if (this.isPanelShowing) {
      return this.wasListEmptyBeforePanelShow;
    }
    return this.nTotalListItems === 0;
  }

  knowledgeListId = `CVEditKnowledgeList${idCounter()}`;

  onChange = action((newValue: CVEditKnowledge[]) => {
    const store = this.knowledgeField.store;

    const oldValue = store.value;

    const oldKnowledgeTypeIds = _.uniq(oldValue.map((v) => v.knowledgeTypeId));
    const newKnowledgeTypeIds = _.uniq(newValue.map((v) => v.knowledgeTypeId));

    const addedKnowledgeTypeIds = _.difference(
      newKnowledgeTypeIds,
      oldKnowledgeTypeIds
    );
    const removedKnowledgeTypeIds = _.difference(
      oldKnowledgeTypeIds,
      newKnowledgeTypeIds
    );

    const mutate = () => {
      const removeOperations = removedKnowledgeTypeIds.map(
        (knowledgeTypeId) => {
          return candidateAPI.removeKnowledgeByTypeFromCandidate({
            showSentryDialogOnError: true,
            userInfo: this.userInfo,
            firmId: this.userInfo.idFirm,
            knowledgeTypeId: knowledgeTypeId,
          });
        }
      );

      const addOperations = addedKnowledgeTypeIds.map((knowledgeTypeId) => {
        return candidateAPI.addKnowledgeToCandidate({
          showSentryDialogOnError: true,
          userInfo: this.userInfo,
          firmId: this.userInfo.idFirm,
          candidateId: this.userInfo.idCandidate,
          knowledgeType: Number(knowledgeTypeId),
          comment: "",
          level: 0,
          expirationDate: null,
          duplicateCheck: false,
        });
      });

      const operations = [...removeOperations, ...addOperations];
      if (operations.length > 0) return Promise.all(operations);
    };

    const invalidate = () =>
      invalidateQueries(this.queryClient, [
        "candidateAPI.getKnowledgeForCandidate",
      ]);

    const onlyRemoving =
      removedKnowledgeTypeIds.length > 0 && addedKnowledgeTypeIds.length === 0;

    const onlyAdding =
      removedKnowledgeTypeIds.length === 0 && addedKnowledgeTypeIds.length > 0;

    // we can do many add operations at the same time,
    // also we can do many remove operations at the same time.
    // but we cannot add and remove at the same time
    let concurrentGroup: string;

    if (onlyRemoving) {
      concurrentGroup = "concurrentlyRemoveKnowledgeByTypeFromCandidate";
    }
    if (onlyAdding) {
      concurrentGroup = "concurrentlyAddKnowledgeToCandidate";
    }

    return this.knowledgeField.valueVersioner.onChange({
      newValue,
      mutateBackend: mutate,
      invalidateQuery: invalidate,
      concurrentMutationGroup: concurrentGroup,
    });
  });

  treeSelectOnChange: TreeSelectProps["onChange"] = action((evt) => {
    evt.originalEvent.persist();

    const [_newValue, clickedOnGroup] = treeSelectOnChange(evt);
    if (clickedOnGroup) return;

    const currentKnowledgeTypesMap: {
      [knowledgeTypeId: string]: CVEditKnowledge;
    } = {};

    this.knowledgeField.store.value.forEach((knowledge) => {
      currentKnowledgeTypesMap[knowledge.knowledgeTypeId.toString()] =
        knowledge;
    });

    const allKnowledgeTypesMap: {
      [knowledgeTypeId: string]: CVEditKnowledge;
    } = {};

    this.groupedKnowledgeTypes.forEach((group) => {
      group.children.forEach((type) => {
        allKnowledgeTypesMap[type.key] = {
          id: 0,
          knowledgeType: type.label,
          knowledgeTypeId: type.value,
          knowledgeFolder: group.label,
        };
      });
    });

    const newValue: CVEditKnowledge[] = Object.keys(_newValue).map(
      (knowledgeTypeId) => {
        if (currentKnowledgeTypesMap[knowledgeTypeId]) {
          return currentKnowledgeTypesMap[knowledgeTypeId];
        }

        const val = allKnowledgeTypesMap[knowledgeTypeId];
        val.id = -idCounter();

        return val;
      }
    );

    return this.onChange(newValue);
  });

  @observable knowledgeField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: [] as CVEditKnowledge[],
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(
      store,
      333,
      false,
      "knowledgeField"
    );

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.knowledge;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () =>
            (data?.items || [])
              .filter((k) => k.knowledgeFolder.toLowerCase() !== "språk")
              .map((knowledge) => {
                const mapped: CVEditKnowledge = {
                  id: knowledge.id,
                  knowledgeTypeId: knowledge.knowledgeTypeId,
                  knowledgeType: knowledge.knowledgeType
                    .replace("<font>", "")
                    .replace("</font>", "")
                    .trim(),
                  knowledgeFolder: knowledge.knowledgeFolder,
                };

                return mapped;
              }),
        };
      })
    );

    return {
      store,
      id: "Knowledge",
      getLabel: () => "",
      valueVersioner,
      hasUnsavedChanges: () => false,
      onRenderComponent: observer(() => {
        const L = useLocStore(LocContext.CV);
        const knowledgeSegmentStore = _this;

        const defaultTreeSelectProps = useDefaultTreeSelectProps();

        return (
          <StyledTreeSelect
            {...defaultTreeSelectProps}
            className={concatClassnames([
              "p-button p-button-outlined cve-knowledge-multiselect",

              knowledgeSegmentStore.showPlaceholder &&
                "cve-empty-list-placeholder cve-empty-education-placeholder",
            ])}
            onShow={action(() => {
              knowledgeSegmentStore.isPanelShowing = true;
              knowledgeSegmentStore.wasListEmptyBeforePanelShow =
                knowledgeSegmentStore.nTotalListItems === 0;

              const list = document.getElementById(this.knowledgeListId);
              const height = list?.clientHeight || 0;

              knowledgeSegmentStore.knowledgeListForcedHeight = `${height}px`;
            })}
            onHide={action(() => {
              knowledgeSegmentStore.isPanelShowing = false;
              knowledgeSegmentStore.knowledgeListForcedHeight = undefined;
            })}
            options={knowledgeSegmentStore.groupedKnowledgeTypes}
            value={store.value.reduce((acc, k) => {
              acc[k.knowledgeTypeId] = true;
              return acc;
            }, {})}
            onChange={this.treeSelectOnChange}
            label={L.id("cb626c3209db").loc("Legg til kunnskap")}
          />
        );
      }),
    };
  });
}

const StyledTreeSelect = styled(TreeSelect)<{ label: string }>`
  &.cve-knowledge-multiselect {
    padding: 0.5rem 0.75rem;

    .p-multiselect-trigger {
      order: -1;
      margin-left: 0.75rem;
      width: auto;
      .p-multiselect-trigger-icon:before {
        content: "\\e90d";
        color: var(--primary-color);
      }
    }

    .p-treeselect-label-container {
      > * {
        display: none;
      }

      &:after {
        content: "${(p) => p.label}";
      }
    }

    .p-treeselect-trigger-icon {
      &:before {
        content: "\\e90d";
        color: var(--primary-color);
      }
    }

    .p-treeselect-trigger {
      order: -1;
      margin: 0 0.5rem 0 0;
      width: auto;
    }
  }
`;
